import { decodeWords } from '../mime-enc/decode-words';
import { StringDecoder } from '../types';
import { HeaderParams, ParsedHeaderValue } from './parseHeaderValueAndParams.types';

interface IntermediateParamValue {
	charset: string | false;
	values: { nr: number; value: string }[];
}

interface IntermediateHeaderParams {
	[key: string]: string | IntermediateParamValue;
}

/**
 * Parses a header value with key=value arguments into a structured object.
 * Handles RFC 2231 special encoding for parameter values.
 * Also handles normal RFC 2047 MIME encoded-words in the main header value
 * and in parameter values (even though not allowed by RFC 2047)
 *
 *   parseHeaderValue('content-type: text/plain; CHARSET='UTF-8'') ->
 *   {
 *     'value': 'text/plain',
 *     'params': {
 *       'charset': 'UTF-8'
 *     }
 *   }
 *
 * @param {String} str Header value
 * @return {Object} Header value as a parsed structure
 */
export function parseHeaderValueAndParams(
	str: string,
	decodeString: StringDecoder
): ParsedHeaderValue {
	let value = '';
	const params1: HeaderParams = {};

	{
		let stage = 'value';
		let key: string | false = false;
		let val = '';
		let quote: string | false = false;
		let escaped = false;

		for (let i = 0, len = str.length; i < len; i++) {
			const chr = str.charAt(i);
			if (stage === 'key') {
				if (chr === '=') {
					key = val.trim().toLowerCase();
					stage = 'value';
					val = '';
					continue;
				}
				val += chr;
			} else {
				if (escaped) {
					val += chr;
				} else if (chr === '\\') {
					escaped = true;
					continue;
				} else if (quote && chr === quote) {
					quote = false;
				} else if (!quote && chr === '"') {
					quote = chr;
				} else if (!quote && chr === ';') {
					if (key === false) {
						value = val.trim();
					} else {
						params1[key] = val.trim();
					}
					stage = 'key';
					val = '';
				} else {
					val += chr;
				}
				escaped = false;
			}
		}

		if (stage === 'value') {
			if (key === false) {
				value = val.trim();
			} else {
				params1[key] = val.trim();
			}
		} else if (val.trim()) {
			params1[val.trim().toLowerCase()] = '';
		}
	}

	// handle parameter value continuations
	// https://tools.ietf.org/html/rfc2231#section-3

	// Now merge the multiple params as necessary
	const params2: IntermediateHeaderParams = {};

	for (const key in params1) {
		const val = params1[key];

		const match = key.match(/\*((\d+)\*?)?$/);
		if (match) {
			const actualKey = key.slice(0, match.index);
			const nr = Number(match[2]) || 0;

			// Get existing params object
			let paramStruct = params2[actualKey];
			if (!paramStruct || typeof paramStruct !== 'object') {
				paramStruct = {
					charset: false,
					values: [],
				};
				params2[actualKey] = paramStruct;
			}

			let val2 = val;

			// If this param value ends with *, then it may have encoding & language
			if (nr === 0 && match[0].charAt(match[0].length - 1) === '*') {
				const match2 = val.match(/^([^']*)'[^']*'(.*)$/);
				if (match2) {
					paramStruct.charset = match2[1] || 'utf-8';
					val2 = match2[2];
				}
			}

			paramStruct.values.push({ nr, value: val2 });
		} else {
			// Treat as basic key / value
			params2[key] = val;
		}
	}

	// concatenate split rfc2231 strings and convert encoded strings to mime encoded words
	const params3: HeaderParams = {};
	for (const key in params2) {
		const val = params2[key];
		let valStr: string;

		if (val && typeof val !== 'string') {
			const _strVal = val.values
				.sort((a, b) => a.nr - b.nr)
				.map(_val => _val.value)
				.join('');

			if (val.charset) {
				const charset = val.charset;

				valStr = _strVal.replace(/(?:%[0-9A-Fa-f]{2})+/g, hexSeq => {
					const numBytes = hexSeq.length / 3;

					const buf = Buffer.allocUnsafe(numBytes);

					for (let i = 0; i < numBytes; i++) {
						const byte = Number.parseInt(hexSeq.slice(i * 3 + 1, i * 3 + 3), 16);
						buf[i] = byte;
					}

					return decodeString(buf, charset);
				});
			} else {
				// Not legal but does occur
				valStr = decodeWords(_strVal, decodeString);
			}
		} else {
			// Not legal but does occur
			valStr = decodeWords(val, decodeString);
		}

		params3[key] = valStr;
	}

	return {
		// Not sure if the main value here should ever have encoded-words...
		value: decodeWords(value, decodeString),
		params: params3,
	};
}
